import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useWishlist } from '../contexts/WishlistContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Heart, ShoppingCart, Sparkles, ChevronRight, Target } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const GOALS = [
  { id: 'weight_loss',            icon: '⚖️', en: 'Weight Loss',           es: 'Pérdida de Peso',       ru: 'Потеря Веса',     de: 'Gewichtsverlust',         fr: 'Perte de poids',          it: 'Perdita di peso',         pl: 'Utrata wagi',             nl: 'Gewichtsverlies',           pt: 'Perda de peso' },
  { id: 'muscle_building',        icon: '💪', en: 'Muscle Building',       es: 'Construcción Muscular', ru: 'Набор Массы',     de: 'Muskelaufbau',            fr: 'Construction musculaire', it: 'Costruzione muscolare',   pl: 'Budowanie mięśni',        nl: 'Spieropbouw',               pt: 'Construção muscular' },
  { id: 'anti_aging',             icon: '✨', en: 'Anti-Aging',            es: 'Anti-Envejecimiento',   ru: 'Антивозрастное',  de: 'Anti-Aging',              fr: 'Anti-âge',                it: 'Anti-invecchiamento',     pl: 'Anti-aging',              nl: 'Anti-aging',                pt: 'Antienvelhecimento' },
  { id: 'cognitive_enhancement',  icon: '🧠', en: 'Cognitive Enhancement', es: 'Mejora Cognitiva',      ru: 'Когнитивное',     de: 'Kognitive Leistung',      fr: 'Amélioration cognitive',  it: 'Potenziamento cognitivo', pl: 'Funkcje poznawcze',       nl: 'Cognitieve verbetering',    pt: 'Melhoria cognitiva' },
  { id: 'recovery_healing',       icon: '🩹', en: 'Recovery & Healing',    es: 'Recuperación',          ru: 'Восстановление',  de: 'Erholung & Heilung',      fr: 'Récupération & guérison', it: 'Recupero & guarigione',   pl: 'Regeneracja',             nl: 'Herstel & genezing',        pt: 'Recuperação' },
  { id: 'immune_support',         icon: '🛡️', en: 'Immune Support',        es: 'Apoyo Inmunológico',    ru: 'Иммунитет',       de: 'Immununterstützung',      fr: 'Soutien immunitaire',     it: 'Supporto immunitario',    pl: 'Wsparcie odporności',     nl: 'Immuunondersteuning',       pt: 'Apoio imunitário' },
  { id: 'hormonal_balance',       icon: '⚡', en: 'Hormonal Balance',      es: 'Equilibrio Hormonal',   ru: 'Гормональный',    de: 'Hormonelles Gleichgewicht', fr: 'Équilibre hormonal',    it: 'Equilibrio ormonale',     pl: 'Równowaga hormonalna',    nl: 'Hormonale balans',          pt: 'Equilíbrio hormonal' },
  { id: 'skin_health',            icon: '🌸', en: 'Skin Health',           es: 'Salud de la Piel',      ru: 'Здоровье Кожи',   de: 'Hautgesundheit',          fr: 'Santé de la peau',        it: 'Salute della pelle',      pl: 'Zdrowie skóry',           nl: 'Huidgezondheid',            pt: 'Saúde da pele' }
];

const RecommendationsPage = () => {
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const { user, isAuthenticated } = useAuth();
  const { currentLanguage, t } = useLanguage();
  const { isInWishlist, toggleWishlist } = useWishlist();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch recommendations
  useEffect(() => {
    const fetchRecommendations = async () => {
      setLoading(true);
      try {
        const goalParam = selectedGoal || (user?.goals?.[0]);
        const url = goalParam 
          ? `${API_URL}/api/recommendations?goal=${goalParam}`
          : `${API_URL}/api/recommendations`;
          
        const response = await fetch(url, { credentials: 'include' });
        const data = await response.json();
        setRecommendations(data.products || []);
        
        if (!selectedGoal && data.goal) {
          setSelectedGoal(data.goal);
        }
      } catch (err) {
        console.error('Error fetching recommendations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [selectedGoal, user, API_URL]);

  const getGoalName = (goal) => {
    return goal[currentLanguage] || goal.en;
  };

  const getGoalById = (goalId) => {
    return GOALS.find(g => g.id === goalId);
  };

  const formatPrice = useCurrency().formatPrice;

  const getProductName = (product) => {
    return product.name[currentLanguage] || product.name.en || product.name;
  };

  const hasVariants = (product) => product.variants && product.variants.length > 1;

  const getDisplayPrice = (product) => {
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price);
      return Math.min(...prices);
    }
    return product.price;
  };

  const translations = {
    en: {
      title: 'Personalized Recommendations',
      subtitle: 'Based on your goals, we recommend these products',
      selectGoal: 'Select a goal to see recommendations',
      yourGoals: 'Your Goals',
      browseAll: 'Browse All Products',
      from: 'From',
      multipleOptions: 'Multiple options available',
      viewProduct: 'View Product',
      addToWishlist: 'Add to Wishlist',
      removeFromWishlist: 'Remove from Wishlist'
    },
    es: {
      title: 'Recomendaciones Personalizadas',
      subtitle: 'Según tus objetivos, te recomendamos estos productos',
      selectGoal: 'Selecciona un objetivo para ver recomendaciones',
      yourGoals: 'Tus Objetivos',
      browseAll: 'Ver Todos los Productos',
      from: 'Desde',
      multipleOptions: 'Múltiples opciones disponibles',
      viewProduct: 'Ver Producto',
      addToWishlist: 'Añadir a Favoritos',
      removeFromWishlist: 'Quitar de Favoritos'
    },
    ru: {
      title: 'Персональные Рекомендации',
      subtitle: 'Исходя из ваших целей, мы рекомендуем эти продукты',
      selectGoal: 'Выберите цель для рекомендаций',
      yourGoals: 'Ваши Цели',
      browseAll: 'Все Продукты',
      from: 'От',
      multipleOptions: 'Доступно несколько вариантов',
      viewProduct: 'Смотреть',
      addToWishlist: 'В избранное',
      removeFromWishlist: 'Убрать из избранного'
    },
    de: {
      title: 'Personalisierte Empfehlungen',
      subtitle: 'Basierend auf Ihren Zielen empfehlen wir diese Produkte',
      selectGoal: 'Wählen Sie ein Ziel, um Empfehlungen zu sehen',
      yourGoals: 'Ihre Ziele',
      browseAll: 'Alle Produkte ansehen',
      from: 'Ab',
      multipleOptions: 'Mehrere Optionen verfügbar',
      viewProduct: 'Produkt ansehen',
      addToWishlist: 'Zur Wunschliste hinzufügen',
      removeFromWishlist: 'Aus der Wunschliste entfernen'
    },
    fr: {
      title: 'Recommandations personnalisées',
      subtitle: 'En fonction de vos objectifs, nous recommandons ces produits',
      selectGoal: 'Sélectionnez un objectif pour voir les recommandations',
      yourGoals: 'Vos objectifs',
      browseAll: 'Voir tous les produits',
      from: 'À partir de',
      multipleOptions: 'Plusieurs options disponibles',
      viewProduct: 'Voir le produit',
      addToWishlist: 'Ajouter à la liste de souhaits',
      removeFromWishlist: 'Retirer de la liste de souhaits'
    },
    it: {
      title: 'Raccomandazioni personalizzate',
      subtitle: 'In base ai tuoi obiettivi, ti consigliamo questi prodotti',
      selectGoal: 'Seleziona un obiettivo per vedere le raccomandazioni',
      yourGoals: 'I tuoi obiettivi',
      browseAll: 'Sfoglia tutti i prodotti',
      from: 'Da',
      multipleOptions: 'Più opzioni disponibili',
      viewProduct: 'Vedi prodotto',
      addToWishlist: 'Aggiungi alla lista dei desideri',
      removeFromWishlist: 'Rimuovi dalla lista dei desideri'
    },
    pl: {
      title: 'Spersonalizowane rekomendacje',
      subtitle: 'Na podstawie Twoich celów polecamy te produkty',
      selectGoal: 'Wybierz cel, aby zobaczyć rekomendacje',
      yourGoals: 'Twoje cele',
      browseAll: 'Przeglądaj wszystkie produkty',
      from: 'Od',
      multipleOptions: 'Dostępnych jest wiele opcji',
      viewProduct: 'Zobacz produkt',
      addToWishlist: 'Dodaj do listy życzeń',
      removeFromWishlist: 'Usuń z listy życzeń'
    },
    nl: {
      title: 'Persoonlijke aanbevelingen',
      subtitle: 'Op basis van uw doelen raden wij deze producten aan',
      selectGoal: 'Selecteer een doel om aanbevelingen te zien',
      yourGoals: 'Uw doelen',
      browseAll: 'Alle producten bekijken',
      from: 'Vanaf',
      multipleOptions: 'Meerdere opties beschikbaar',
      viewProduct: 'Product bekijken',
      addToWishlist: 'Aan verlanglijst toevoegen',
      removeFromWishlist: 'Verwijderen van verlanglijst'
    },
    pt: {
      title: 'Recomendações Personalizadas',
      subtitle: 'Com base nos seus objetivos, recomendamos estes produtos',
      selectGoal: 'Selecione um objetivo para ver recomendações',
      yourGoals: 'Os seus objetivos',
      browseAll: 'Ver todos os produtos',
      from: 'A partir de',
      multipleOptions: 'Várias opções disponíveis',
      viewProduct: 'Ver produto',
      addToWishlist: 'Adicionar à lista de desejos',
      removeFromWishlist: 'Remover da lista de desejos'
    }
  };

  const pageTranslations = translations[currentLanguage] || translations.en;

  const selectedGoalData = selectedGoal ? getGoalById(selectedGoal) : null;

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />
      
      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="recsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#recsGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#recsGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-10 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20">
              <Sparkles className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t('rec_ai_powered')}</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{pageTranslations.title}</h1>
            <p className="text-lg text-gray-300">{pageTranslations.subtitle}</p>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 -mt-8">
        {/* Goal Selection */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-capsule-teal" />
            {pageTranslations.yourGoals}
          </h2>
          <div className="flex flex-wrap gap-3">
            {GOALS.map((goal) => (
              <button
                key={goal.id}
                onClick={() => setSelectedGoal(goal.id)}
                className={`px-4 py-2 rounded-full border-2 font-medium transition-all flex items-center gap-2 ${
                  selectedGoal === goal.id
                    ? 'border-capsule-teal bg-capsule-teal/10 text-capsule-teal shadow-md'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-capsule-teal/50 hover:bg-capsule-teal/5'
                }`}
                data-testid={`goal-filter-${goal.id}`}
              >
                <span>{goal.icon}</span>
                <span className="text-sm">{getGoalName(goal)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Selected Goal Indicator */}
        {selectedGoalData && (
          <div className="mb-6 p-4 bg-gradient-to-r from-capsule-teal/10 to-capsule-aqua/10 rounded-2xl border border-capsule-teal/20">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{selectedGoalData.icon}</span>
              <div>
                <p className="text-sm text-capsule-teal font-medium">{t('rec_showing_for')}</p>
                <p className="text-lg font-semibold text-gray-900">{getGoalName(selectedGoalData)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-capsule-teal"></div>
          </div>
        ) : recommendations.length === 0 ? (
          <Card className="shadow-lg border-0 rounded-2xl">
            <CardContent className="text-center py-12">
              <div className="w-20 h-20 bg-capsule-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-10 h-10 text-capsule-teal" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{pageTranslations.selectGoal}</h3>
              <p className="text-gray-600 mb-6">{t('rec_choose_goal')}</p>
              <Link to="/products">
                <Button className="bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl">
                  {pageTranslations.browseAll}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {recommendations.map((product) => (
              <Card 
                key={product.id}
                className="group shadow-lg border-0 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300"
                data-testid={`recommendation-${product.id}`}
              >
                <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100">
                  <img 
                    src={product.image_url}
                    alt={getProductName(product)}
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                  />
                  {product.is_new && (
                    <span className="absolute top-3 left-3 bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      NEW
                    </span>
                  )}
                  {isAuthenticated && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        toggleWishlist(product.id);
                      }}
                      className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all ${
                        isInWishlist(product.id)
                          ? 'bg-capsule-salmon/20 text-capsule-salmon'
                          : 'bg-white/90 text-gray-400 hover:text-capsule-salmon'
                      }`}
                      title={isInWishlist(product.id) ? pageTranslations.removeFromWishlist : pageTranslations.addToWishlist}
                    >
                      <Heart className={`w-5 h-5 ${isInWishlist(product.id) ? 'fill-current' : ''}`} />
                    </button>
                  )}
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[3rem]">
                    {getProductName(product)}
                  </h3>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      {hasVariants(product) && (
                        <span className="text-xs text-gray-500">{pageTranslations.from}</span>
                      )}
                      <span className="text-lg font-bold text-capsule-teal ml-1">
                        {formatPrice(getDisplayPrice(product))}
                      </span>
                    </div>
                  </div>
                  
                  <Link to={`/product/${product.id}`}>
                    <Button className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {pageTranslations.viewProduct}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Browse All Button */}
        {recommendations.length > 0 && (
          <div className="text-center mt-8">
            <Link to="/products">
              <Button variant="outline" className="border-capsule-teal text-capsule-teal hover:bg-capsule-teal/10 rounded-xl">
                {pageTranslations.browseAll}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default RecommendationsPage;
